// @ts-check
import { LitElement, html, css } from 'lit';
import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons';

export class VoyageFloatPanel extends LitElement {
    constructor() {
        super();
        // The guards work similar to margin but for movement in the viewport
        // relative to this component's left, top, right and visible-top (from top).
        this.guardTop = 6;
        this.guardRight = 6;
        this.guardBottom = 6;
        this.guardLeft = 6;
        this.visibleTop = 100;

        // Default: Go to upper left corner.
        this.xpos = this.guardLeft;
        this.ypos = this.guardTop;
        this._width = 0;

        this.dragged = false;
        this.transparent = false;
        this.panelTitle = "";
        this.showBackButton = false;

        this.collapsed = false;

        this.startPos = {x:this.xpos,y:this.ypos,ox:this.xpos,oy:this.ypos};

        this.onResize = this.onResize.bind(this);

        this._afId = 0;
    }

    static get properties() {
        return {
            xpos: { type: Number },
            ypos: { type: Number },
            dragged: { type: Boolean, reflect: true },
            transparent: { type: Boolean, reflect: true },
            guardTop: { type: Number, attribute: 'guard-top' },
            guardRight: { type: Number, attribute: 'guard-right' },
            guardBottom: { type: Number, attribute: 'guard-bottom' },
            guardLeft: { type: Number, attribute: 'guard-left' },
            visibleTop: { type: Number, attribute: 'visible-top' },
            panelTitle: { type: String },
            showBackButton: { type: Boolean, attribute: 'show-back-button' },
            collapsed: { type: Boolean }
        };
    }

    updated(props) {
        props.forEach((oldValue, propName) => {
            if (propName.indexOf('guard') == 0) {
                this.onResize();
            }
        });
    }

    static get styles() {
        return [
            css`
            :host {
                display: block;
                position: fixed;
                box-sizing: border-box;

                background: var(--neutrals-grey-10, white);
                border-radius: 3px;
                border: 1px solid var(--neutrals-grey-06, black);
                box-shadow: 5px 5px 10px rgba(0,0,0,0.5);
            }

            :host([dragged]) {
                box-shadow: 15px 15px 20px rgba(0,0,0,0.5);
            }

            :host([transparent]) {
                opacity: 0.5;
            }

            :host([transparent]:hover) {
                opacity: 1;
            }

            #topbar {
                display: flex;
                align-items: center;
                position: relative;
                width: 100%;
                min-height: 40px;
                touch-action: none;
                cursor: grab;
            }

            #topbar.dragging {
                cursor: move;
            }

            #topbar[has-title] {
                background-color: var(--neutrals-grey-09);
            }

            .title {
                font-family: MaerskText;
                font-size: 24px;
                font-weight: bold;
                line-height: 32px;
                margin: 10px;
                margin-left: 15px;
                margin-right: 15px;
                flex-grow: 1;
                color: var(--neutrals-grey-02, black);
            }

            .iconButton {
                background: none;
                border: none;
                outline: none;
                cursor: pointer;
            }
            
            .iconButton > svg {
                display: block;
                fill: currentColor;
            }

            .back {
                margin-left: 10px;
                color: var(--neutrals-grey-02, black);
            }

            .back > svg {
                height: 17px;
                width: 10px;
            }

            .collapse {
                margin-right: 15px;
                color: var(--neutrals-grey-04, grey);
            }

            .collapse > svg {
                width: 16px;
            }

            .collapsedButton {
                display: flex;
                align-items: center;
                background: none;
                border: none;
                outline: none;
                cursor: pointer;
                padding: 15px;
                font-family: MaerskText;
                font-size: 14px;
                color: var(--neutrals-grey-03, black);
                background-color: var(--neutrals-grey-09, white);

            }

            .collapsedButton > svg {
                display: block;
                width: 16px;
                margin-right: 10px;
                fill: var(--neutrals-grey-02, black);
            }
        `];
    }

    render(){

        this.style.setProperty("top", `${this.ypos}px`);
        this.style.setProperty("left", `${this.xpos}px`);

        if (this.collapsed) {
            return html`
            <button class="collapsedButton" @click=${() => this.collapsed = false}>${SvgIcons.lightArrowToRight}Expand Sidebar</button>
            `;
        }

        return html`
        <div id="topbar" @pointerup=${this.pup} @pointerdown=${this.pdown} @pointermove=${this.drag} class="${this.dragged ? 'dragging' : ''}" ?has-title=${!!this.panelTitle}>
            ${this.panelTitle ? html`
            ${this.showBackButton ? html`
            <button class="iconButton back" @click=${() => this.dispatchEvent(new Event('back-clicked'))} @pointerdown=${this.stopPropagation} @pointermove=${this.stopPropagation}>${SvgIcons.chevronLeft}</button>
            ` : html``}
            <h3 class="title">${this.panelTitle}</h3>
            ` : html`
            <div class="title"></div>
            `}
            <button class="iconButton collapse" @click=${() => this.collapse()} @pointerdown=${this.stopPropagation} @pointermove=${this.stopPropagation}>${SvgIcons.lightArrowToLeft}</button>
        </div>
        <div class="container"><slot></slot></div>
        `;
    }

    pdown(evt) {
        this.startPos = {x:evt.pageX,y:evt.pageY,ox:this.xpos,oy:this.ypos};
        evt.target.setPointerCapture(evt.pointerId);
        this.dragged = true;
        evt.preventDefault();
        return false;
    }

    pup(evt) {
        evt.target.releasePointerCapture(evt.pointerId);
        this.dragged = false;
        evt.preventDefault();
        return false;
    }

    setGuardedPos(xpos, ypos) {
        const maxX = window.innerWidth - (this.guardRight + this._width);
        const maxY = window.innerHeight - (this.guardBottom + this.visibleTop);

        // Use guards
        if (xpos < this.guardLeft) {
            xpos = this.guardLeft;
        } else if (xpos > maxX) {
            xpos = maxX;
        }
        if (ypos < this.guardTop) {
            ypos = this.guardTop;
        } else if (ypos > maxY) {
            ypos = maxY;
        }

        this.xpos = xpos;
        this.ypos = ypos;
    }

    onResize() {
        if (this._afId !== 0) {
            return;
        }

        // Wait for the next frame, otherwise the size may not be ready yet
        this._afId = requestAnimationFrame( () => {
            this._width = this.getBoundingClientRect().width;
            this.setGuardedPos(this.xpos, this.ypos);
            this._afId = 0;
        });
    }

    drag(evt) {
        if (evt.pressure > 0.2) {
            let xpos = this.startPos.ox + evt.pageX - this.startPos.x;
            let ypos = this.startPos.oy + evt.pageY - this.startPos.y;
            

            this.setGuardedPos(xpos, ypos);

            evt.preventDefault();
            return false;
        }
    }

    firstUpdated() {
        const ro = new ResizeObserver( () => this.onResize() );

        ro.observe(this, { box: "border-box" });
        this.onResize();
    }

    connectedCallback() {
        super.connectedCallback();
        window.addEventListener('resize', this.onResize);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        window.removeEventListener('resize', this.onResize);
    }

    stopPropagation(e) {
        e.stopPropagation();
    }

    collapse() {
        this.collapsed = true;
        this.setGuardedPos(0, 0);
    }
}
customElements.define('voyage-float-panel', VoyageFloatPanel);

